import { translateBackend } from '@assets/i18n/config';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import type { TranslatedString } from '@interfaces/shared';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Form } from '@ovComponents/2-component/form/form';
import { AmountField } from '@ovComponents/3-pattern/amountField/amountField';
import { SubAccountSelect } from '@ovComponents/3-pattern/subaccountSelect/subAccountSelect';
import { dollars2cents } from '@utils/dollars2cents';
import { Box, Button, Grid, Typography, useTheme } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export type SubAccountTransfer = {
  subAccount?: SubAccount;
  fromSubAccountId?: string;
  amountCents?: number;
  toSubAccountId?: string;
  subAccountIn?: SubAccount;
};

type SubAccountTransferDialogContentProps = {
  onTransferCreated: (transfer: SubAccountTransfer) => void;
  userData: any;
  onDialogClose: () => void;
  disclaimer?: TranslatedString | null;
  options?: any;
};

const SubAccountTransferDialogContent = ({
  onTransferCreated,
  userData,
  onDialogClose,
  disclaimer,
}: SubAccountTransferDialogContentProps) => {
  const { t } = useTranslation([
    'workflowCompletions',
    'affiliationTypes',
    'components',
  ]);
  const [inboundAccount, setInboundAccount] = useState<
    SubAccount | undefined
  >();
  const [outboundAccount, setOutboundAccount] = useState<
    SubAccount | undefined
  >();
  const [amount, setAmount] = useState('');
  const theme = useTheme();

  const submit = () => {
    onTransferCreated({
      amountCents: dollars2cents(amount),
      fromSubAccountId: outboundAccount?.id ?? '',
      toSubAccountId: inboundAccount?.id ?? '',
      subAccount: outboundAccount,
      subAccountIn: inboundAccount,
    });
  };
  return (
    <Form onSubmit={submit}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'end',
        }}
      >
        <Box
          onClick={onDialogClose}
          sx={{
            display: 'flex',
            cursor: 'pointer',
          }}
        >
          <Icon icon={CloseRoundedIcon} size='medium' />
        </Box>
      </Box>
      <Typography variant='headingMedium' sx={{ mt: 1 }}>
        {t('workflowCompletions:subAccountTransferCreation.title')}
      </Typography>
      <SubAccountSelect
        sx={{ mt: '24px' }}
        omitAny
        selectedSubAccount={outboundAccount}
        onSubAccountSelect={setOutboundAccount}
        userId={userData.id}
        label={t('workflowCompletions:subAccountTransferCreation.from')}
      />
      <SubAccountSelect
        sx={{ mt: '24px' }}
        selectedSubAccount={inboundAccount}
        omitAny
        onSubAccountSelect={setInboundAccount}
        userId={userData.id}
        label={t('workflowCompletions:subAccountTransferCreation.to')}
        subAccountsToExclude={[outboundAccount?.id ?? '']}
      />
      <Grid
        container
        spacing={1}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'end',
          mt: '20px',
          mb: '24px',
        }}
      >
        <Grid size={4}>
          <AmountField amount={amount} setAmount={setAmount} />
        </Grid>
      </Grid>
      {disclaimer && (
        <Box
          sx={{
            mb: 1,
            background: theme.color.surfaceContainerVariant,
            borderRadius: theme.borderRadius.lg,
            p: 2,
          }}
        >
          <Typography variant='bodySmall' component='div'>
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackend(disclaimer)}
            </ReactMarkdown>
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        <Button color='primary' disabled={false} sx={{ mt: 3 }} type='submit'>
          {t('workflowCompletions:addTransfer')}
        </Button>
      </Box>
    </Form>
  );
};

export default SubAccountTransferDialogContent;
